import { createSelector } from 'reselect'
import { AppGlobalState, Progress } from '../../types'
import { ContactLifeCycleActivity } from 'modules/domain/contactLifeCycleActivity/types'
import { EntityMetadata } from 'modules/domain/types'

const contactLifeCycleActivitiesDict = (state: AppGlobalState) => state.contactLifeCycleActivity.items
const ids = (state: AppGlobalState) => state.contactLifeCycleActivity.ids
const contactLifeCycleActivity = (state: AppGlobalState, id: string): ContactLifeCycleActivity | undefined =>
  state.contactLifeCycleActivity.items[id]
const meta = (state: AppGlobalState, id: string): EntityMetadata<ContactLifeCycleActivity> =>
  state.contactLifeCycleActivity.meta[id] ||
  state.contactLifeCycleActivity.meta[
    Object.values(state.contactLifeCycleActivity.items).find(
      (contactLifeCycleActivity) => contactLifeCycleActivity.id === id,
    )?.id || ''
  ] || {
    fetchError: null,
    fetchProgress: Progress.IDLE,
    id,
    removeError: null,
    removeProgress: Progress.IDLE,
    updateError: null,
    updateProgress: Progress.IDLE,
  }

const itemFetchProgress = (state: AppGlobalState) => state.contactLifeCycleActivity.itemFetchProgress
const listFetchProgress = (state: AppGlobalState) => state.contactLifeCycleActivity.listFetchProgress
const listFetchNextProgress = (state: AppGlobalState) => state.contactLifeCycleActivity.listFetchNextProgress
const listFetchNextError = (state: AppGlobalState) => state.contactLifeCycleActivity.listFetchNextError
const listFetchError = (state: AppGlobalState) => state.contactLifeCycleActivity.listFetchError
const contactLifeCycleActivityList = createSelector(
  contactLifeCycleActivitiesDict,
  ids,
  listFetchProgress,
  (dict, ids, progress) => {
    if (progress === Progress.WORK) {
      return []
    }
    const result: ContactLifeCycleActivity[] = []
    for (const id of ids) {
      result.push(dict[id])
    }
    return result
  },
)

const filter = (state: AppGlobalState) => state.contactLifeCycleActivity.filter
const sorting = (state: AppGlobalState) => state.contactLifeCycleActivity.sorting
const page = (state: AppGlobalState) => state.contactLifeCycleActivity.page
const total = (state: AppGlobalState) => state.contactLifeCycleActivity.total
const updateProgress = (state: AppGlobalState) => state.contactLifeCycleActivity.updateProgress
const updateError = (state: AppGlobalState) => state.contactLifeCycleActivity.updateError
const updateErrorDetail = (state: AppGlobalState) => state.contactLifeCycleActivity.updateErrorDetail
const removeProgress = (state: AppGlobalState) => state.contactLifeCycleActivity.removeProgress
const addProgress = (state: AppGlobalState) => state.contactLifeCycleActivity.addProgress
const addError = (state: AppGlobalState) => state.contactLifeCycleActivity.addError
const addErrorDetail = (state: AppGlobalState) => state.contactLifeCycleActivity.addErrorDetail
const hasNext = (state: AppGlobalState) =>
  state.contactLifeCycleActivity.total > state.contactLifeCycleActivity.ids.length
const pageSize = (state: AppGlobalState) => state.contactLifeCycleActivity.pageSize
const pages = (state: AppGlobalState) =>
  Math.ceil(state.contactLifeCycleActivity.total / state.contactLifeCycleActivity.pageSize)

const ContactLifeCycleActivitySelectors = {
  filter,
  sorting,
  page,
  total,
  updateProgress,
  removeProgress,
  addProgress,
  addError,
  addErrorDetail,
  updateError,
  updateErrorDetail,
  contactLifeCycleActivitiesDict,
  ids,
  contactLifeCycleActivity,
  meta,
  itemFetchProgress,
  listFetchProgress,
  listFetchError,
  contactLifeCycleActivityList,
  hasNext,
  pageSize,
  listFetchNextProgress,
  listFetchNextError,
  pages,
}

export default ContactLifeCycleActivitySelectors
